/** Персональная палитра интерфейса. Цвета данных и общая сетка таблицы живут отдельно. */
export type ThemeMode = 'light' | 'dark';

export const COLOR_TOKEN_GROUPS = [
  {
    label: 'Фоны и панели',
    tokens: [
      { key: 'app-bg', label: 'Фон приложения' },
      { key: 'app-header', label: 'Шапка приложения' },
      { key: 'background', label: 'Окна и диалоги' },
      { key: 'card', label: 'Карточки' },
      { key: 'popover', label: 'Всплывающие меню' },
      { key: 'grid-background', label: 'Фон таблиц' },
      { key: 'grid-header', label: 'Заголовки таблиц' },
    ],
  },
  {
    label: 'Текст',
    tokens: [
      { key: 'foreground', label: 'Основной текст' },
      { key: 'card-foreground', label: 'Текст карточек' },
      { key: 'popover-foreground', label: 'Текст меню' },
      { key: 'muted-foreground', label: 'Подписи и пояснения' },
    ],
  },
  {
    label: 'Кнопки и акценты',
    tokens: [
      { key: 'primary', label: 'Основные кнопки' },
      { key: 'primary-foreground', label: 'Текст основных кнопок' },
      { key: 'secondary', label: 'Второстепенные кнопки' },
      { key: 'secondary-foreground', label: 'Текст второстепенных кнопок' },
      { key: 'muted', label: 'Приглушённый фон' },
      { key: 'accent', label: 'Фон при наведении' },
      { key: 'accent-foreground', label: 'Текст при наведении' },
      { key: 'destructive', label: 'Кнопки удаления и ошибки ячеек' },
      { key: 'success', label: 'Успешное сохранение ячейки' },
      { key: 'warning', label: 'Обновление ячейки' },
    ],
  },
  {
    label: 'Таблицы, рамки и выделение',
    tokens: [
      { key: 'border', label: 'Рамки и разделители' },
      { key: 'input', label: 'Рамки полей ввода' },
      { key: 'ring', label: 'Фокус с клавиатуры' },
      { key: 'selection', label: 'Выделение ячеек' },
      { key: 'row-selected', label: 'Выделенная строка' },
      { key: 'row-stripe', label: 'Чередование строк' },
      { key: 'sheet-cell-border', label: 'Ручная рамка ячейки' },
      { key: 'scroll-thumb', label: 'Полоса прокрутки' },
      { key: 'scroll-thumb-hover', label: 'Прокрутка при наведении' },
    ],
  },
  {
    label: 'Диаграммы',
    tokens: [
      { key: 'chart-1', label: 'Цвет 1' },
      { key: 'chart-2', label: 'Цвет 2' },
      { key: 'chart-3', label: 'Цвет 3' },
      { key: 'chart-4', label: 'Цвет 4' },
      { key: 'chart-5', label: 'Цвет 5' },
    ],
  },
] as const;

export type ColorToken = (typeof COLOR_TOKEN_GROUPS)[number]['tokens'][number]['key'];
export type ColorPalette = Record<ColorToken, string>;
export type ColorSchemeSettings = {
  preset: string;
  custom: Record<ThemeMode, ColorPalette>;
};

export const COLOR_TOKENS: readonly ColorToken[] = COLOR_TOKEN_GROUPS.flatMap((group) =>
  group.tokens.map((token) => token.key),
);
export const COLOR_SCHEME_STORAGE_KEY = 'yana.colorScheme';
export const COLOR_SCHEME_CSS_STORAGE_KEY = 'yana.colorScheme.css';

const standardLight: ColorPalette = {
  'app-bg': '#f9fafb',
  'app-header': '#ffffff',
  background: '#ffffff',
  foreground: '#111827',
  card: '#ffffff',
  'card-foreground': '#111827',
  popover: '#ffffff',
  'popover-foreground': '#111827',
  'grid-background': '#ffffff',
  'grid-header': '#f1f5f9',
  primary: '#2563eb',
  'primary-foreground': '#ffffff',
  secondary: '#f1f5f9',
  'secondary-foreground': '#1e293b',
  muted: '#f1f5f9',
  'muted-foreground': '#64748b',
  accent: '#f1f5f9',
  'accent-foreground': '#1e293b',
  destructive: '#dc2626',
  success: '#22c55e',
  warning: '#f59e0b',
  border: '#e2e8f0',
  input: '#cbd5e1',
  ring: '#3b82f6',
  selection: '#2563eb',
  'row-selected': '#dbeafe',
  'row-stripe': '#f8fafc',
  'sheet-cell-border': '#475569',
  'scroll-thumb': '#cbd5e1',
  'scroll-thumb-hover': '#94a3b8',
  'chart-1': '#3b82f6',
  'chart-2': '#8b5cf6',
  'chart-3': '#22c55e',
  'chart-4': '#f59e0b',
  'chart-5': '#dc2626',
};

const standardDark: ColorPalette = {
  'app-bg': '#020617',
  'app-header': '#0f172a',
  background: '#0f172a',
  foreground: '#f1f5f9',
  card: '#0f172a',
  'card-foreground': '#f1f5f9',
  popover: '#0f172a',
  'popover-foreground': '#f1f5f9',
  'grid-background': '#0f172a',
  'grid-header': '#1e293b',
  primary: '#3b82f6',
  'primary-foreground': '#020617',
  secondary: '#1e293b',
  'secondary-foreground': '#f1f5f9',
  muted: '#1e293b',
  'muted-foreground': '#94a3b8',
  accent: '#1e293b',
  'accent-foreground': '#f1f5f9',
  destructive: '#f87171',
  success: '#4ade80',
  warning: '#fbbf24',
  border: '#2c3344',
  input: '#3a4150',
  ring: '#3b82f6',
  selection: '#3b82f6',
  'row-selected': '#1d3256',
  'row-stripe': '#182134',
  'sheet-cell-border': '#93979f',
  'scroll-thumb': '#3a4150',
  'scroll-thumb-hover': '#575d6a',
  'chart-1': '#60a5fa',
  'chart-2': '#a78bfa',
  'chart-3': '#4ade80',
  'chart-4': '#fbbf24',
  'chart-5': '#f87171',
};

function palette(base: ColorPalette, overrides: Partial<ColorPalette>): ColorPalette {
  return { ...base, ...overrides };
}

type PaletteSeed = {
  canvas: string;
  surface: string;
  text: string;
  mutedText: string;
  primary: string;
  charts: readonly [string, string, string, string, string];
};

/** Общая шкала поверхностей сохраняет оттенок темы во всех элементах интерфейса. */
function themedPalette(mode: ThemeMode, seed: PaletteSeed): ColorPalette {
  const { canvas, surface, text, mutedText, primary, charts } = seed;
  const muted = mix(surface, text, mode === 'dark' ? 0.07 : 0.055);
  const input = mix(surface, text, 0.24);
  return palette(mode === 'dark' ? standardDark : standardLight, {
    'app-bg': canvas,
    'app-header': surface,
    background: surface,
    card: surface,
    popover: surface,
    'grid-background': surface,
    'grid-header': muted,
    foreground: text,
    'card-foreground': text,
    'popover-foreground': text,
    'muted-foreground': mutedText,
    primary,
    'primary-foreground': mode === 'dark' ? canvas : '#ffffff',
    secondary: muted,
    'secondary-foreground': text,
    muted,
    accent: mix(surface, primary, 0.1),
    'accent-foreground': text,
    border: mix(surface, text, 0.14),
    input,
    ring: primary,
    selection: primary,
    'row-selected': mix(surface, primary, 0.2),
    'row-stripe': mix(surface, text, 0.025),
    'sheet-cell-border': mutedText,
    'scroll-thumb': input,
    'scroll-thumb-hover': mix(surface, text, 0.4),
    'chart-1': charts[0],
    'chart-2': charts[1],
    'chart-3': charts[2],
    'chart-4': charts[3],
    'chart-5': charts[4],
  });
}

export const COLOR_SCHEME_PRESETS: ReadonlyArray<{
  id: string;
  label: string;
  description: string;
  light: ColorPalette;
  dark: ColorPalette;
}> = [
  {
    id: 'standard',
    label: 'Стандартная',
    description: 'Нейтральные поверхности и синие акценты',
    light: standardLight,
    dark: standardDark,
  },
  {
    id: 'emerald',
    label: 'Изумрудная',
    description: 'Мягкие зелёные оттенки и изумрудные кнопки',
    light: palette(standardLight, {
      'app-bg': '#f3f8f6',
      'app-header': '#fafffc',
      background: '#fafffc',
      card: '#fafffc',
      popover: '#fafffc',
      'grid-background': '#fafffc',
      'grid-header': '#e8f2ed',
      foreground: '#173b30',
      'card-foreground': '#173b30',
      'popover-foreground': '#173b30',
      primary: '#047857',
      ring: '#10b981',
      selection: '#059669',
      secondary: '#e8f2ed',
      'secondary-foreground': '#235342',
      muted: '#e8f2ed',
      'muted-foreground': '#587568',
      accent: '#dceee5',
      'accent-foreground': '#235342',
      border: '#cfdfd6',
      input: '#b0cbbd',
      'row-selected': '#c8ecdc',
      'row-stripe': '#f0f7f3',
      'sheet-cell-border': '#4e7462',
      'scroll-thumb': '#b0cbbd',
      'scroll-thumb-hover': '#7fa58f',
      'chart-1': '#059669',
      'chart-2': '#0d9488',
      'chart-3': '#65a30d',
      'chart-4': '#d97706',
    }),
    dark: palette(standardDark, {
      'app-bg': '#071a14',
      'app-header': '#10281f',
      background: '#10281f',
      card: '#10281f',
      popover: '#10281f',
      'grid-background': '#10281f',
      'grid-header': '#1d3b2e',
      foreground: '#e8f5ed',
      'card-foreground': '#e8f5ed',
      'popover-foreground': '#e8f5ed',
      primary: '#34d399',
      'primary-foreground': '#06281b',
      ring: '#34d399',
      selection: '#34d399',
      secondary: '#1d3b2e',
      'secondary-foreground': '#e8f5ed',
      muted: '#1d3b2e',
      'muted-foreground': '#96b8a5',
      accent: '#254b3a',
      'accent-foreground': '#e8f5ed',
      border: '#305040',
      input: '#466b56',
      'row-selected': '#15543e',
      'row-stripe': '#163125',
      'sheet-cell-border': '#8eb49e',
      'scroll-thumb': '#466b56',
      'scroll-thumb-hover': '#648c73',
      'chart-1': '#34d399',
      'chart-2': '#2dd4bf',
      'chart-3': '#a3e635',
      'chart-4': '#fbbf24',
    }),
  },
  {
    id: 'violet',
    label: 'Фиолетовая',
    description: 'Светлая лаванда и выразительные фиолетовые акценты',
    light: palette(standardLight, {
      'app-bg': '#f7f5fc',
      'app-header': '#fdfcff',
      background: '#fdfcff',
      card: '#fdfcff',
      popover: '#fdfcff',
      'grid-background': '#fdfcff',
      'grid-header': '#f0ebf8',
      foreground: '#302044',
      'card-foreground': '#302044',
      'popover-foreground': '#302044',
      primary: '#7c3aed',
      ring: '#8b5cf6',
      selection: '#7c3aed',
      secondary: '#f0ebf8',
      'secondary-foreground': '#4c3264',
      muted: '#f0ebf8',
      'muted-foreground': '#7b6a91',
      accent: '#e8def8',
      'accent-foreground': '#4c3264',
      border: '#e0d6ed',
      input: '#c9b9dd',
      'row-selected': '#e5d8fc',
      'row-stripe': '#f6f1fb',
      'sheet-cell-border': '#7c628f',
      'scroll-thumb': '#c9b9dd',
      'scroll-thumb-hover': '#a28bbd',
      'chart-1': '#8b5cf6',
      'chart-2': '#d946ef',
      'chart-3': '#14b8a6',
      'chart-4': '#f59e0b',
    }),
    dark: palette(standardDark, {
      'app-bg': '#160e24',
      'app-header': '#221831',
      background: '#221831',
      card: '#221831',
      popover: '#221831',
      'grid-background': '#221831',
      'grid-header': '#332443',
      foreground: '#f1eafa',
      'card-foreground': '#f1eafa',
      'popover-foreground': '#f1eafa',
      primary: '#a78bfa',
      'primary-foreground': '#211336',
      ring: '#a78bfa',
      selection: '#a78bfa',
      secondary: '#332443',
      'secondary-foreground': '#f1eafa',
      muted: '#332443',
      'muted-foreground': '#b3a0c8',
      accent: '#433057',
      'accent-foreground': '#f1eafa',
      border: '#4a375d',
      input: '#634c78',
      'row-selected': '#4b2b78',
      'row-stripe': '#2a1d3a',
      'sheet-cell-border': '#b09ac5',
      'scroll-thumb': '#634c78',
      'scroll-thumb-hover': '#836895',
      'chart-1': '#a78bfa',
      'chart-2': '#e879f9',
      'chart-3': '#2dd4bf',
      'chart-4': '#fbbf24',
    }),
  },
  {
    id: 'sand',
    label: 'Песочная',
    description: 'Тёплые поверхности и янтарные акценты',
    light: palette(standardLight, {
      'app-bg': '#f8f5ef',
      'app-header': '#fffdf8',
      background: '#fffdf8',
      card: '#fffdf8',
      popover: '#fffdf8',
      'grid-background': '#fffdf8',
      'grid-header': '#f2eadc',
      foreground: '#3d3020',
      'card-foreground': '#3d3020',
      'popover-foreground': '#3d3020',
      primary: '#a16207',
      ring: '#ca8a04',
      selection: '#b7790c',
      secondary: '#f2eadc',
      'secondary-foreground': '#58452b',
      muted: '#f2eadc',
      'muted-foreground': '#85745b',
      accent: '#eedec5',
      'accent-foreground': '#58452b',
      border: '#e3d7c2',
      input: '#ccba9b',
      'row-selected': '#f5e2b1',
      'row-stripe': '#faf3e8',
      'sheet-cell-border': '#8c7451',
      'scroll-thumb': '#ccba9b',
      'scroll-thumb-hover': '#ad9570',
      'chart-1': '#b7790c',
      'chart-2': '#c26b42',
      'chart-3': '#6c8b46',
      'chart-4': '#8872b2',
    }),
    dark: palette(standardDark, {
      'app-bg': '#1b160f',
      'app-header': '#2a2116',
      background: '#2a2116',
      card: '#2a2116',
      popover: '#2a2116',
      'grid-background': '#2a2116',
      'grid-header': '#3c3021',
      foreground: '#f7eedc',
      'card-foreground': '#f7eedc',
      'popover-foreground': '#f7eedc',
      primary: '#e8b85e',
      'primary-foreground': '#302008',
      ring: '#e8b85e',
      selection: '#e8b85e',
      secondary: '#3c3021',
      'secondary-foreground': '#f7eedc',
      muted: '#3c3021',
      'muted-foreground': '#c0ab88',
      accent: '#50402b',
      'accent-foreground': '#f7eedc',
      border: '#574630',
      input: '#715c3f',
      'row-selected': '#654719',
      'row-stripe': '#332819',
      'sheet-cell-border': '#c1a578',
      'scroll-thumb': '#715c3f',
      'scroll-thumb-hover': '#947d58',
      'chart-1': '#e8b85e',
      'chart-2': '#ef9977',
      'chart-3': '#a8c67e',
      'chart-4': '#b6a2df',
    }),
  },
  {
    id: 'graphite',
    label: 'Графитовая',
    description: 'Спокойная серая палитра без цветных акцентов',
    light: palette(standardLight, {
      'app-bg': '#f5f5f5',
      'grid-header': '#eeeeef',
      foreground: '#242426',
      'card-foreground': '#242426',
      'popover-foreground': '#242426',
      primary: '#52525b',
      ring: '#71717a',
      selection: '#52525b',
      secondary: '#eeeeef',
      'secondary-foreground': '#3f3f46',
      muted: '#eeeeef',
      'muted-foreground': '#71717a',
      accent: '#e4e4e7',
      'accent-foreground': '#3f3f46',
      border: '#dedee2',
      input: '#c9c9cf',
      'row-selected': '#dcdce2',
      'row-stripe': '#f7f7f8',
      'sheet-cell-border': '#62626c',
      'scroll-thumb': '#c9c9cf',
      'scroll-thumb-hover': '#a1a1aa',
      'chart-1': '#52525b',
      'chart-2': '#7a6a91',
      'chart-3': '#4a8478',
      'chart-4': '#b18b4b',
    }),
    dark: palette(standardDark, {
      'app-bg': '#111113',
      'app-header': '#1c1c1f',
      background: '#1c1c1f',
      card: '#1c1c1f',
      popover: '#1c1c1f',
      'grid-background': '#1c1c1f',
      'grid-header': '#2b2b30',
      foreground: '#f0f0f2',
      'card-foreground': '#f0f0f2',
      'popover-foreground': '#f0f0f2',
      primary: '#d4d4d8',
      'primary-foreground': '#18181b',
      ring: '#a1a1aa',
      selection: '#a1a1aa',
      secondary: '#2b2b30',
      'secondary-foreground': '#f0f0f2',
      muted: '#2b2b30',
      'muted-foreground': '#aaaab4',
      accent: '#393940',
      'accent-foreground': '#f0f0f2',
      border: '#424249',
      input: '#585860',
      'row-selected': '#41414c',
      'row-stripe': '#232327',
      'sheet-cell-border': '#a5a5b0',
      'scroll-thumb': '#585860',
      'scroll-thumb-hover': '#767680',
      'chart-1': '#d4d4d8',
      'chart-2': '#b6a6d1',
      'chart-3': '#84bcaf',
      'chart-4': '#d4b77e',
    }),
  },
  {
    id: 'ocean',
    label: 'Океан',
    description: 'Прохладные бирюзовые акценты и глубокие морские оттенки',
    light: themedPalette('light', {
      canvas: '#f0f7f9',
      surface: '#fbfeff',
      text: '#163641',
      mutedText: '#526a74',
      primary: '#0e7490',
      charts: ['#0891b2', '#0d9488', '#6366f1', '#d97706', '#e11d48'],
    }),
    dark: themedPalette('dark', {
      canvas: '#071c24',
      surface: '#102630',
      text: '#e8f4f8',
      mutedText: '#a5c0ca',
      primary: '#22d3ee',
      charts: ['#22d3ee', '#5eead4', '#a5b4fc', '#fbbf24', '#fb7185'],
    }),
  },
  {
    id: 'sky',
    label: 'Небесная',
    description: 'Воздушные голубые поверхности и чистый синий акцент',
    light: themedPalette('light', {
      canvas: '#f0f7fe',
      surface: '#fbfdff',
      text: '#1b354b',
      mutedText: '#526b80',
      primary: '#0369a1',
      charts: ['#0284c7', '#6366f1', '#0d9488', '#ea580c', '#c026d3'],
    }),
    dark: themedPalette('dark', {
      canvas: '#0b1b2b',
      surface: '#142631',
      text: '#e9f3fc',
      mutedText: '#a6bfd3',
      primary: '#7dd3fc',
      charts: ['#7dd3fc', '#a5b4fc', '#5eead4', '#fdba74', '#f0abfc'],
    }),
  },
  {
    id: 'rose',
    label: 'Розовая',
    description: 'Пудровые поверхности и насыщенные ягодные акценты',
    light: themedPalette('light', {
      canvas: '#fcf3f7',
      surface: '#fffbfd',
      text: '#472537',
      mutedText: '#7a586b',
      primary: '#be185d',
      charts: ['#db2777', '#9333ea', '#0d9488', '#d97706', '#2563eb'],
    }),
    dark: themedPalette('dark', {
      canvas: '#21121c',
      surface: '#2d1926',
      text: '#fbeaf2',
      mutedText: '#ccaabb',
      primary: '#f9a8d4',
      charts: ['#f9a8d4', '#d8b4fe', '#5eead4', '#fcd34d', '#93c5fd'],
    }),
  },
  {
    id: 'coral',
    label: 'Коралловая',
    description: 'Тёплый персиковый фон и живые коралловые кнопки',
    light: themedPalette('light', {
      canvas: '#fff4ef',
      surface: '#fffcfa',
      text: '#4b2c27',
      mutedText: '#805f57',
      primary: '#c24135',
      charts: ['#dd624d', '#d97706', '#0d9488', '#7c3aed', '#0284c7'],
    }),
    dark: themedPalette('dark', {
      canvas: '#221312',
      surface: '#2d1c1b',
      text: '#fcece7',
      mutedText: '#d0afa5',
      primary: '#fbad96',
      charts: ['#fbad96', '#fcd34d', '#5eead4', '#c4b5fd', '#7dd3fc'],
    }),
  },
  {
    id: 'forest',
    label: 'Лесная',
    description: 'Природные зелёные оттенки с мягким мшистым акцентом',
    light: themedPalette('light', {
      canvas: '#f3f7ee',
      surface: '#fcfef9',
      text: '#293d25',
      mutedText: '#5c7053',
      primary: '#3f6b35',
      charts: ['#4d7c3e', '#0f766e', '#b7791f', '#a85586', '#547ba1'],
    }),
    dark: themedPalette('dark', {
      canvas: '#111d10',
      surface: '#1b2819',
      text: '#eef5e8',
      mutedText: '#b1c4a5',
      primary: '#a3cf8b',
      charts: ['#a3cf8b', '#5eead4', '#e8bf71', '#e8a0ca', '#93bde6'],
    }),
  },
  {
    id: 'coffee',
    label: 'Кофейная',
    description: 'Молочные поверхности и спокойные оттенки какао',
    light: themedPalette('light', {
      canvas: '#f7f2ed',
      surface: '#fffdf9',
      text: '#443129',
      mutedText: '#776257',
      primary: '#80533b',
      charts: ['#956548', '#a8873f', '#588073', '#8a6da8', '#b85e67'],
    }),
    dark: themedPalette('dark', {
      canvas: '#211713',
      surface: '#2c211d',
      text: '#f7ede5',
      mutedText: '#c8b3a6',
      primary: '#d6ae8d',
      charts: ['#d6ae8d', '#ddc17f', '#94c5b4', '#c1a7df', '#e99ea5'],
    }),
  },
  {
    id: 'olive',
    label: 'Оливковая',
    description: 'Светлый лён и сдержанные оливковые акценты',
    light: themedPalette('light', {
      canvas: '#f6f7ed',
      surface: '#fefef8',
      text: '#363b24',
      mutedText: '#686d50',
      primary: '#63712a',
      charts: ['#78863b', '#b08835', '#36827e', '#a95c58', '#7772a6'],
    }),
    dark: themedPalette('dark', {
      canvas: '#1a1d12',
      surface: '#25281b',
      text: '#f2f4df',
      mutedText: '#bfc3a3',
      primary: '#c4ce78',
      charts: ['#c4ce78', '#e5c376', '#86c9c2', '#eaa09a', '#b9b3e0'],
    }),
  },
];

export function defaultColorScheme(): ColorSchemeSettings {
  return {
    preset: 'standard',
    custom: { light: { ...standardLight }, dark: { ...standardDark } },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Только непрозрачный HEX: значение одинаково безопасно для CSS, ECharts и color input. */
export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const color = value.trim();
  if (/^#[\da-f]{6}$/i.test(color)) return color.toLowerCase();
  if (/^#[\da-f]{3}$/i.test(color)) {
    return (
      '#' +
      [...color.slice(1)]
        .map((digit) => digit + digit)
        .join('')
        .toLowerCase()
    );
  }
  return null;
}

/** Битая настройка восстанавливается по отдельным цветам, неизвестные поля отбрасываются. */
export function normalizeColorScheme(value: unknown): ColorSchemeSettings {
  const result = defaultColorScheme();
  if (!isRecord(value)) return result;
  if (value.preset === 'custom' || COLOR_SCHEME_PRESETS.some((p) => p.id === value.preset)) {
    result.preset = value.preset as string;
  }
  if (!isRecord(value.custom)) return result;
  for (const mode of ['light', 'dark'] as const) {
    const source = value.custom[mode];
    if (!isRecord(source)) continue;
    for (const token of COLOR_TOKENS) {
      const color = normalizeHexColor(source[token]);
      if (color) result.custom[mode][token] = color;
    }
  }
  return result;
}

export function resolveColorScheme(settings: ColorSchemeSettings, mode: ThemeMode): ColorPalette {
  const normalized = normalizeColorScheme(settings);
  if (normalized.preset === 'custom') return { ...normalized.custom[mode] };
  const preset = COLOR_SCHEME_PRESETS.find((entry) => entry.id === normalized.preset)!;
  return { ...preset[mode] };
}

function mix(color: string, other: string, amount: number): string {
  return (
    '#' +
    [1, 3, 5]
      .map((index) => {
        const a = parseInt(color.slice(index, index + 2), 16);
        const b = parseInt(other.slice(index, index + 2), 16);
        return Math.round(a * (1 - amount) + b * amount)
          .toString(16)
          .padStart(2, '0');
      })
      .join('')
  );
}

export function colorSchemeCssVars(colors: ColorPalette): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const token of COLOR_TOKENS) vars[`--${token}`] = colors[token];
  return {
    ...vars,
    '--color-brand-50': mix(colors.primary, colors.card, 0.92),
    // 100 и 300 используются для текста на тёмных акцентах, поэтому всегда
    // светлые. Непрозрачная заливка строки имеет отдельный row-selected.
    '--color-brand-100': mix(colors.primary, '#ffffff', 0.8),
    '--color-brand-300': mix(colors.primary, '#ffffff', 0.5),
    '--color-brand-400': mix(colors.ring, '#ffffff', 0.2),
    '--color-brand-500': colors.ring,
    '--color-brand-600': colors.primary,
    '--color-brand-700': mix(colors.primary, '#000000', 0.18),
    '--pinned-shadow-color': '#00000040',
    '--sidebar': colors.card,
    '--sidebar-foreground': colors['card-foreground'],
    '--sidebar-primary': colors.primary,
    '--sidebar-primary-foreground': colors['primary-foreground'],
    '--sidebar-accent': colors.accent,
    '--sidebar-accent-foreground': colors['accent-foreground'],
    '--sidebar-border': colors.border,
    '--sidebar-ring': colors.ring,
  };
}

/** Белый список разделяется со скриптом в index.html и проверяется тестом. */
export const COLOR_SCHEME_CSS_NAMES = Object.keys(colorSchemeCssVars(standardLight));
